/** API profil görseli yolunu Next.js proxy adresine çevirir. */
export function resolveNetworkProfileMediaUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  if (url.startsWith('data:')) return url
  if (url.startsWith('/api/opas/')) return url
  if (url.startsWith('/api/pharmacy-network/')) {
    return url.replace(/^\/api\/pharmacy-network\//, '/api/opas/pharmacy-network/')
  }
  return url
}

/** Duvar paylaşım/yorum görseli yolunu Next.js proxy adresine çevirir. */
export function resolveNetworkPostMediaUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  if (url.startsWith('data:')) return url
  if (url.startsWith('/api/opas/')) return url
  if (url.startsWith('/api/pharmacy-posts/')) {
    return url.replace(/^\/api\/pharmacy-posts\//, '/api/opas/pharmacy-posts/')
  }
  return url
}

export function networkUserKey(tenantId: string, userId: string) {
  return `${tenantId}:${userId}`
}

export function networkUserAvatarSrc(tenantId: string, userId: string, version?: string | null): string {
  const base = `/api/opas/pharmacy-network/profile/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}/avatar`
  return version ? `${base}?v=${version}` : base
}

const USER_STORAGE_KEY = 'opas_pn_user_media_v1'

type UserMediaCacheEntry = { av?: string; noAv?: 1 }

let userMemoryCache: Record<string, UserMediaCacheEntry> | null = null

function readUserCache(): Record<string, UserMediaCacheEntry> {
  if (userMemoryCache) return userMemoryCache
  if (typeof window === 'undefined') {
    userMemoryCache = {}
    return userMemoryCache
  }
  try {
    const raw = sessionStorage.getItem(USER_STORAGE_KEY)
    userMemoryCache = raw ? (JSON.parse(raw) as Record<string, UserMediaCacheEntry>) : {}
  } catch {
    userMemoryCache = {}
  }
  return userMemoryCache
}

function persistUserCache() {
  if (typeof window === 'undefined' || !userMemoryCache) return
  try {
    sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userMemoryCache))
  } catch {
    /* quota */
  }
}

export function rememberNetworkUserAvatarUrl(
  tenantId: string,
  userId: string,
  avatarUrl?: string | null
) {
  const key = networkUserKey(tenantId, userId)
  const cache = readUserCache()
  const prev = cache[key] ?? {}
  const next: UserMediaCacheEntry = { ...prev }
  let changed = false

  if (avatarUrl === null) {
    next.noAv = 1
    delete next.av
    changed = true
  } else if (avatarUrl && !avatarUrl.startsWith('data:')) {
    const version = parseMediaVersion(resolveNetworkProfileMediaUrl(avatarUrl) ?? avatarUrl)
    if (version) {
      next.av = version
      delete next.noAv
      changed = true
    }
  } else if (avatarUrl?.startsWith('data:')) {
    delete next.noAv
    changed = true
  }

  if (changed) {
    cache[key] = next
    persistUserCache()
  }
}

export function getNetworkUserAvatarSrc(tenantId: string, userId: string): string | undefined {
  const entry = readUserCache()[networkUserKey(tenantId, userId)] ?? {}
  if (entry.noAv) return undefined
  return networkUserAvatarSrc(tenantId, userId, entry.av)
}

export function markNetworkUserAvatarMissing(tenantId: string, userId: string) {
  const cache = readUserCache()
  const key = networkUserKey(tenantId, userId)
  cache[key] = { ...cache[key], noAv: 1 }
  delete cache[key]?.av
  persistUserCache()
}

export function markNetworkUserAvatarPresent(tenantId: string, userId: string, version?: string) {
  const cache = readUserCache()
  const key = networkUserKey(tenantId, userId)
  const next: UserMediaCacheEntry = { ...cache[key] }
  delete next.noAv
  if (version) next.av = version
  cache[key] = next
  persistUserCache()
}

export function resolveNetworkUserAvatarDisplaySrc(
  tenantId: string,
  userId: string,
  avatarUrl?: string | null
): string | undefined {
  if (avatarUrl?.startsWith('data:')) return avatarUrl
  if (avatarUrl) return resolveNetworkProfileMediaUrl(avatarUrl)
  return getNetworkUserAvatarSrc(tenantId, userId)
}

const STORAGE_KEY = 'opas_pn_media_v1'

type MediaCacheEntry = {
  av?: string
  cv?: string
  noAv?: 1
  noCv?: 1
}

let memoryCache: Record<string, MediaCacheEntry> | null = null

function readCache(): Record<string, MediaCacheEntry> {
  if (memoryCache) return memoryCache
  if (typeof window === 'undefined') {
    memoryCache = {}
    return memoryCache
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    memoryCache = raw ? (JSON.parse(raw) as Record<string, MediaCacheEntry>) : {}
  } catch {
    memoryCache = {}
  }
  return memoryCache
}

function persistCache() {
  if (typeof window === 'undefined' || !memoryCache) return
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memoryCache))
  } catch {
    /* quota */
  }
}

function cacheEntry(tenantId: string): MediaCacheEntry {
  return readCache()[tenantId] ?? {}
}

export function parseMediaVersion(url: string | null | undefined): string | undefined {
  if (!url) return undefined
  const match = url.match(/[?&]v=(\d+)/)
  return match?.[1]
}

export function networkAvatarSrc(tenantId: string, version?: string | null): string {
  const base = `/api/opas/pharmacy-network/profile/${encodeURIComponent(tenantId)}/avatar`
  return version ? `${base}?v=${version}` : base
}

export function networkCoverSrc(tenantId: string, version?: string | null): string {
  const base = `/api/opas/pharmacy-network/profile/${encodeURIComponent(tenantId)}/cover`
  return version ? `${base}?v=${version}` : base
}

export function getNetworkAvatarSrc(tenantId: string): string | undefined {
  const entry = cacheEntry(tenantId)
  if (entry.noAv) return undefined
  return networkAvatarSrc(tenantId, entry.av)
}

export function getNetworkCoverSrc(tenantId: string): string | undefined {
  const entry = cacheEntry(tenantId)
  if (entry.noCv) return undefined
  return networkCoverSrc(tenantId, entry.cv)
}

export function rememberProfileMediaUrls(
  tenantId: string,
  avatarUrl?: string | null,
  coverUrl?: string | null
) {
  const cache = readCache()
  const prev = cache[tenantId] ?? {}
  const next: MediaCacheEntry = { ...prev }
  let changed = false

  if (avatarUrl === null) {
    next.noAv = 1
    delete next.av
    changed = true
  } else if (avatarUrl && !avatarUrl.startsWith('data:')) {
    const resolved = resolveNetworkProfileMediaUrl(avatarUrl)
    const version = parseMediaVersion(resolved ?? avatarUrl)
    if (version) {
      next.av = version
      delete next.noAv
      changed = true
    } else if (resolved) {
      delete next.noAv
      changed = true
    }
  } else if (avatarUrl?.startsWith('data:')) {
    delete next.noAv
    changed = true
  }

  if (coverUrl === null) {
    next.noCv = 1
    delete next.cv
    changed = true
  } else if (coverUrl && !coverUrl.startsWith('data:')) {
    const resolved = resolveNetworkProfileMediaUrl(coverUrl)
    const version = parseMediaVersion(resolved ?? coverUrl)
    if (version) {
      next.cv = version
      delete next.noCv
      changed = true
    } else if (resolved) {
      delete next.noCv
      changed = true
    }
  } else if (coverUrl?.startsWith('data:')) {
    delete next.noCv
    changed = true
  }

  if (changed) {
    cache[tenantId] = next
    persistCache()
  }
}

export function markNetworkAvatarMissing(tenantId: string) {
  const cache = readCache()
  const next: MediaCacheEntry = { ...cacheEntry(tenantId), noAv: 1 }
  delete next.av
  cache[tenantId] = next
  persistCache()
}

export function markNetworkAvatarPresent(tenantId: string, version?: string) {
  const cache = readCache()
  const next: MediaCacheEntry = { ...cacheEntry(tenantId) }
  delete next.noAv
  if (version) next.av = version
  cache[tenantId] = next
  persistCache()
}

export function resolveNetworkAvatarDisplaySrc(
  tenantId: string,
  avatarUrl?: string | null
): string | undefined {
  if (avatarUrl?.startsWith('data:')) return avatarUrl
  if (avatarUrl) return resolveNetworkProfileMediaUrl(avatarUrl)
  return getNetworkAvatarSrc(tenantId)
}

export function resolveNetworkCoverDisplaySrc(
  tenantId: string,
  coverUrl?: string | null
): string | undefined {
  if (coverUrl?.startsWith('data:')) return coverUrl
  if (coverUrl) return resolveNetworkProfileMediaUrl(coverUrl)
  return getNetworkCoverSrc(tenantId)
}

const prefetchedAvatars = new Set<string>()

export function prefetchNetworkAvatars(tenantIds: string[]) {
  if (typeof window === 'undefined') return
  for (const tenantId of tenantIds) {
    const src = getNetworkAvatarSrc(tenantId)
    if (!src || prefetchedAvatars.has(src)) continue
    prefetchedAvatars.add(src)
    const img = new Image()
    img.onload = () => {
      const version = parseMediaVersion(src)
      markNetworkAvatarPresent(tenantId, version)
    }
    img.onerror = () => markNetworkAvatarMissing(tenantId)
    img.src = src
  }
}

const prefetchedPostImages = new Set<string>()

/** Duvar akışı açılır açılmaz paylaşım görsellerini tarayıcı önbelleğine alır. */
export function prefetchNetworkPostImages(urls: Iterable<string | null | undefined>) {
  if (typeof window === 'undefined') return
  for (const raw of urls) {
    const src = raw ? resolveNetworkPostMediaUrl(raw) ?? raw : undefined
    if (!src || src.startsWith('data:') || prefetchedPostImages.has(src)) continue
    prefetchedPostImages.add(src)
    const img = new Image()
    img.decoding = 'async'
    img.src = src
  }
}

export function collectPostImageUrls(posts: { images?: { url: string }[] | null }[]): string[] {
  const out: string[] = []
  for (const post of posts) {
    for (const img of post.images ?? []) {
      if (img.url) out.push(img.url)
    }
  }
  return out
}

